import { useQuery, useMutation, useQueryClient } from 'react-query';
import ApiService from '@/services/api';
import {
  IPSIListParams,
  IPurchaseCreateRequest,
  IPurchaseUpdateRequest,
  ISaleCreateRequest,
  ISaleUpdateRequest,
  IInventoryCreateRequest,
  IInventoryUpdateRequest,
  IInvoiceCreateRequest,
  IInvoiceUpdateRequest,
  IPartCodeCreateRequest,
  IPartCodeCellUpdateRequest,
  IFinanceCreateRequest,
  IFinanceUpdateRequest,
  IStockTakeGenerateRequest,
  IStockTakeUpdateRequest,
  IStockTakeBatchUpdateRequest,
  IExpenditureCreateRequest,
  IExpenditureUpdateRequest,
} from '@/types/psi';

// ============================================================================
// Query Keys
// ============================================================================

export const PSI_QUERY_KEYS = {
  purchases: {
    all: ['psi', 'purchases'] as const,
    lists: () => [...PSI_QUERY_KEYS.purchases.all, 'list'] as const,
    list: (params: IPSIListParams) => [...PSI_QUERY_KEYS.purchases.lists(), params] as const,
    details: () => [...PSI_QUERY_KEYS.purchases.all, 'detail'] as const,
    detail: (id: string) => [...PSI_QUERY_KEYS.purchases.details(), id] as const,
  },
  sales: {
    all: ['psi', 'sales'] as const,
    lists: () => [...PSI_QUERY_KEYS.sales.all, 'list'] as const,
    list: (params: IPSIListParams) => [...PSI_QUERY_KEYS.sales.lists(), params] as const,
    details: () => [...PSI_QUERY_KEYS.sales.all, 'detail'] as const,
    detail: (id: string) => [...PSI_QUERY_KEYS.sales.details(), id] as const,
  },
  inventory: {
    all: ['psi', 'inventory'] as const,
    lists: () => [...PSI_QUERY_KEYS.inventory.all, 'list'] as const,
    list: (params: IPSIListParams) => [...PSI_QUERY_KEYS.inventory.lists(), params] as const,
    details: () => [...PSI_QUERY_KEYS.inventory.all, 'detail'] as const,
    detail: (sku: string) => [...PSI_QUERY_KEYS.inventory.details(), sku] as const,
  },
  invoices: {
    all: ['psi', 'invoices'] as const,
    lists: () => [...PSI_QUERY_KEYS.invoices.all, 'list'] as const,
    list: (params: IPSIListParams) => [...PSI_QUERY_KEYS.invoices.lists(), params] as const,
    details: () => [...PSI_QUERY_KEYS.invoices.all, 'detail'] as const,
    detail: (invoiceNo: string) => [...PSI_QUERY_KEYS.invoices.details(), invoiceNo] as const,
  },
  partcodes: {
    all: ['psi', 'partcodes'] as const,
    categories: () => [...PSI_QUERY_KEYS.partcodes.all, 'categories'] as const,
    lists: () => [...PSI_QUERY_KEYS.partcodes.all, 'list'] as const,
    list: (categoryId: string) => [...PSI_QUERY_KEYS.partcodes.lists(), categoryId] as const,
  },
  finance: {
    all: ['psi', 'finance'] as const,
    lists: () => [...PSI_QUERY_KEYS.finance.all, 'list'] as const,
    list: (params: IPSIListParams) => [...PSI_QUERY_KEYS.finance.lists(), params] as const,
    details: () => [...PSI_QUERY_KEYS.finance.all, 'detail'] as const,
    detail: (id: string) => [...PSI_QUERY_KEYS.finance.details(), id] as const,
  },
  stocktake: {
    all: ['psi', 'stocktake'] as const,
    sessions: () => [...PSI_QUERY_KEYS.stocktake.all, 'sessions'] as const,
    records: (year: number, month: number) => [...PSI_QUERY_KEYS.stocktake.all, 'records', year, month] as const,
  },
  expenditures: {
    all: ['psi', 'expenditures'] as const,
    lists: () => [...PSI_QUERY_KEYS.expenditures.all, 'list'] as const,
    list: (params: IPSIListParams) => [...PSI_QUERY_KEYS.expenditures.lists(), params] as const,
    details: () => [...PSI_QUERY_KEYS.expenditures.all, 'detail'] as const,
    detail: (id: string) => [...PSI_QUERY_KEYS.expenditures.details(), id] as const,
    summary: (year: number, period: string) => [...PSI_QUERY_KEYS.expenditures.all, 'summary', year, period] as const,
  },
};

// ============================================================================
// Purchases Hooks
// ============================================================================

export const usePurchases = (params: IPSIListParams = {}) => {
  return useQuery(
    PSI_QUERY_KEYS.purchases.list(params),
    () => ApiService.getPurchases(params),
    {
      keepPreviousData: true,
      staleTime: 30000,
      cacheTime: 300000,
    }
  );
};

export const usePurchase = (id: string) => {
  return useQuery(
    PSI_QUERY_KEYS.purchases.detail(id),
    () => ApiService.getPurchase(id),
    {
      enabled: !!id,
      staleTime: 60000,
    }
  );
};

export const useCreatePurchase = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (data: IPurchaseCreateRequest) => ApiService.createPurchase(data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(PSI_QUERY_KEYS.purchases.lists());
      },
    }
  );
};

export const useUpdatePurchase = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ id, data }: { id: string; data: IPurchaseUpdateRequest }) =>
      ApiService.updatePurchase(id, data),
    {
      onSuccess: (_, { id }) => {
        queryClient.invalidateQueries(PSI_QUERY_KEYS.purchases.detail(id));
        queryClient.invalidateQueries(PSI_QUERY_KEYS.purchases.lists());
      },
    }
  );
};

export const useDeletePurchase = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (id: string) => ApiService.deletePurchase(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(PSI_QUERY_KEYS.purchases.lists());
      },
    }
  );
};

// ============================================================================
// Sales Hooks
// ============================================================================

export const useSales = (params: IPSIListParams = {}) => {
  return useQuery(
    PSI_QUERY_KEYS.sales.list(params),
    () => ApiService.getSales(params),
    {
      keepPreviousData: true,
      staleTime: 30000,
      cacheTime: 300000,
    }
  );
};

export const useSale = (id: string) => {
  return useQuery(
    PSI_QUERY_KEYS.sales.detail(id),
    () => ApiService.getSale(id),
    {
      enabled: !!id,
      staleTime: 60000,
    }
  );
};

export const useCreateSale = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (data: ISaleCreateRequest) => ApiService.createSale(data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(PSI_QUERY_KEYS.sales.lists());
      },
    }
  );
};

export const useUpdateSale = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ id, data }: { id: string; data: ISaleUpdateRequest }) =>
      ApiService.updateSale(id, data),
    {
      onSuccess: (_, { id }) => {
        queryClient.invalidateQueries(PSI_QUERY_KEYS.sales.detail(id));
        queryClient.invalidateQueries(PSI_QUERY_KEYS.sales.lists());
      },
    }
  );
};

export const useDeleteSale = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (id: string) => ApiService.deleteSale(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(PSI_QUERY_KEYS.sales.lists());
      },
    }
  );
};

// ============================================================================
// Inventory Hooks
// ============================================================================

export const useInventoryItems = (params: IPSIListParams = {}) => {
  return useQuery(
    PSI_QUERY_KEYS.inventory.list(params),
    () => ApiService.getInventoryItems(params),
    {
      keepPreviousData: true,
      staleTime: 30000,
      cacheTime: 300000,
    }
  );
};

export const useInventoryItem = (sku: string) => {
  return useQuery(
    PSI_QUERY_KEYS.inventory.detail(sku),
    () => ApiService.getInventoryItem(sku),
    {
      enabled: !!sku,
      staleTime: 60000,
    }
  );
};

export const useCreateInventoryItem = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (data: IInventoryCreateRequest) => ApiService.createInventoryItem(data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(PSI_QUERY_KEYS.inventory.lists());
      },
    }
  );
};

export const useUpdateInventoryItem = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ sku, data }: { sku: string; data: IInventoryUpdateRequest }) =>
      ApiService.updateInventoryItem(sku, data),
    {
      onSuccess: (_, { sku }) => {
        queryClient.invalidateQueries(PSI_QUERY_KEYS.inventory.detail(sku));
        queryClient.invalidateQueries(PSI_QUERY_KEYS.inventory.lists());
      },
    }
  );
};

export const useDeleteInventoryItem = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (sku: string) => ApiService.deleteInventoryItem(sku),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(PSI_QUERY_KEYS.inventory.lists());
      },
    }
  );
};

// ============================================================================
// Stock Take Hooks
// ============================================================================

export const useStockTakeSessions = () => {
  return useQuery(
    PSI_QUERY_KEYS.stocktake.sessions(),
    () => ApiService.getStockTakeSessions(),
    {
      staleTime: 60000,
      cacheTime: 300000,
    }
  );
};

export const useStockTakeRecords = (year: number, month: number, options?: { enabled?: boolean }) => {
  return useQuery(
    PSI_QUERY_KEYS.stocktake.records(year, month),
    () => ApiService.getStockTakeRecords(year, month),
    {
      enabled: options?.enabled !== false && year > 0 && month > 0,
      staleTime: 30000,
      cacheTime: 300000,
    }
  );
};

export const useGenerateStockTake = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (data: IStockTakeGenerateRequest) => ApiService.generateStockTake(data),
    {
      onSuccess: (_, { year, month }) => {
        queryClient.invalidateQueries(PSI_QUERY_KEYS.stocktake.sessions());
        queryClient.invalidateQueries(PSI_QUERY_KEYS.stocktake.records(year, month));
      },
    }
  );
};

export const useUpdateStockTakeRecord = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ year, month, id, data }: { year: number; month: number; id: string; data: IStockTakeUpdateRequest }) =>
      ApiService.updateStockTakeRecord(year, month, id, data),
    {
      onSuccess: (_, { year, month }) => {
        queryClient.invalidateQueries(PSI_QUERY_KEYS.stocktake.records(year, month));
        queryClient.invalidateQueries(PSI_QUERY_KEYS.stocktake.sessions());
      },
    }
  );
};

export const useBatchUpdateStockTakeRecords = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ year, month, data }: { year: number; month: number; data: IStockTakeBatchUpdateRequest }) =>
      ApiService.batchUpdateStockTakeRecords(year, month, data),
    {
      onSuccess: (_, { year, month }) => {
        queryClient.invalidateQueries(PSI_QUERY_KEYS.stocktake.records(year, month));
        queryClient.invalidateQueries(PSI_QUERY_KEYS.stocktake.sessions());
      },
    }
  );
};

// ============================================================================
// Invoice Hooks
// ============================================================================

export const useInvoices = (params: IPSIListParams = {}) => {
  return useQuery(
    PSI_QUERY_KEYS.invoices.list(params),
    () => ApiService.getInvoices(params),
    {
      keepPreviousData: true,
      staleTime: 30000,
      cacheTime: 300000,
    }
  );
};

export const useInvoice = (invoiceNo: string) => {
  return useQuery(
    PSI_QUERY_KEYS.invoices.detail(invoiceNo),
    () => ApiService.getInvoice(invoiceNo),
    {
      enabled: !!invoiceNo,
      staleTime: 60000,
    }
  );
};

export const useCreateInvoice = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (data: IInvoiceCreateRequest) => ApiService.createInvoice(data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(PSI_QUERY_KEYS.invoices.lists());
      },
    }
  );
};

export const useUpdateInvoice = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ invoiceNo, data }: { invoiceNo: string; data: IInvoiceUpdateRequest }) =>
      ApiService.updateInvoice(invoiceNo, data),
    {
      onSuccess: (_, { invoiceNo }) => {
        queryClient.invalidateQueries(PSI_QUERY_KEYS.invoices.detail(invoiceNo));
        queryClient.invalidateQueries(PSI_QUERY_KEYS.invoices.lists());
      },
    }
  );
};

export const useDeleteInvoice = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (invoiceNo: string) => ApiService.deleteInvoice(invoiceNo),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(PSI_QUERY_KEYS.invoices.lists());
      },
    }
  );
};

// ============================================================================
// Part Code Hooks
// ============================================================================

export const usePartCodeCategories = () => {
  return useQuery(
    PSI_QUERY_KEYS.partcodes.categories(),
    () => ApiService.getPartCodeCategories(),
    {
      staleTime: 300000, // 5 minutes - categories rarely change
      cacheTime: 600000,
    }
  );
};

export const usePartCodes = (categoryId: string) => {
  return useQuery(
    PSI_QUERY_KEYS.partcodes.list(categoryId),
    () => ApiService.getPartCodes(categoryId),
    {
      enabled: !!categoryId,
      keepPreviousData: true,
      staleTime: 30000,
      cacheTime: 300000,
    }
  );
};

export const useCreatePartCode = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ categoryId, data }: { categoryId: string; data: IPartCodeCreateRequest }) =>
      ApiService.createPartCode(categoryId, data),
    {
      onSuccess: (_, { categoryId }) => {
        queryClient.invalidateQueries(PSI_QUERY_KEYS.partcodes.list(categoryId));
      },
    }
  );
};

// Parts Master (Firestore — Phase 1e)
export const useCreatePart = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (data: Parameters<typeof ApiService.createPart>[0]) =>
      ApiService.createPart(data),
    {
      onSuccess: () => {
        // Invalidate parts-related queries
        queryClient.invalidateQueries(['parts']);
      },
    }
  );
};

export const useUpdatePartCodeCell = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ categoryId, data }: { categoryId: string; data: IPartCodeCellUpdateRequest }) =>
      ApiService.updatePartCodeCell(categoryId, data),
    {
      onSuccess: (_, { categoryId }) => {
        queryClient.invalidateQueries(PSI_QUERY_KEYS.partcodes.list(categoryId));
      },
    }
  );
};

export const useDeletePartCode = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ categoryId, rowIndex }: { categoryId: string; rowIndex: number }) =>
      ApiService.deletePartCode(categoryId, rowIndex),
    {
      onSuccess: (_, { categoryId }) => {
        queryClient.invalidateQueries(PSI_QUERY_KEYS.partcodes.list(categoryId));
      },
    }
  );
};

// ============================================================================
// Finance Hooks (Cash-in / Cash-out)
// ============================================================================

export const useFinanceTransactions = (params: IPSIListParams = {}) => {
  return useQuery(
    PSI_QUERY_KEYS.finance.list(params),
    () => ApiService.getFinanceTransactions(params),
    {
      keepPreviousData: true,
      staleTime: 30000,
      cacheTime: 300000,
    }
  );
};

export const useFinanceTransaction = (id: string) => {
  return useQuery(
    PSI_QUERY_KEYS.finance.detail(id),
    () => ApiService.getFinanceTransaction(id),
    {
      enabled: !!id,
      staleTime: 60000,
    }
  );
};

export const useCreateFinanceTransaction = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (data: IFinanceCreateRequest) => ApiService.createFinanceTransaction(data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(PSI_QUERY_KEYS.finance.lists());
      },
    }
  );
};

export const useUpdateFinanceTransaction = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ id, data }: { id: string; data: IFinanceUpdateRequest }) =>
      ApiService.updateFinanceTransaction(id, data),
    {
      onSuccess: (_, { id }) => {
        queryClient.invalidateQueries(PSI_QUERY_KEYS.finance.detail(id));
        queryClient.invalidateQueries(PSI_QUERY_KEYS.finance.lists());
      },
    }
  );
};

export const useDeleteFinanceTransaction = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (id: string) => ApiService.deleteFinanceTransaction(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(PSI_QUERY_KEYS.finance.lists());
      },
    }
  );
};

// ============================================================================
// Expenditure Hooks (Company Expenses)
// ============================================================================

export const useExpenditures = (params: IPSIListParams = {}) => {
  return useQuery(
    PSI_QUERY_KEYS.expenditures.list(params),
    () => ApiService.getExpenditures(params),
    {
      keepPreviousData: true,
      staleTime: 30000,
      cacheTime: 300000,
    }
  );
};

export const useExpenditure = (id: string) => {
  return useQuery(
    PSI_QUERY_KEYS.expenditures.detail(id),
    () => ApiService.getExpenditure(id),
    {
      enabled: !!id,
      staleTime: 60000,
    }
  );
};

export const useCreateExpenditure = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (data: IExpenditureCreateRequest) => ApiService.createExpenditure(data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(PSI_QUERY_KEYS.expenditures.lists());
      },
    }
  );
};

export const useUpdateExpenditure = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ id, data }: { id: string; data: IExpenditureUpdateRequest }) =>
      ApiService.updateExpenditure(id, data),
    {
      onSuccess: (_, { id }) => {
        queryClient.invalidateQueries(PSI_QUERY_KEYS.expenditures.detail(id));
        queryClient.invalidateQueries(PSI_QUERY_KEYS.expenditures.lists());
      },
    }
  );
};

export const useDeleteExpenditure = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (id: string) => ApiService.deleteExpenditure(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(PSI_QUERY_KEYS.expenditures.lists());
      },
    }
  );
};

export const useExpenditureSummary = (year: number, period: string = 'monthly') => {
  return useQuery(
    PSI_QUERY_KEYS.expenditures.summary(year, period),
    () => ApiService.getExpenditureSummary(year, period),
    {
      enabled: year > 0,
      staleTime: 60000,
      cacheTime: 300000,
    }
  );
};
